import type { TrackingData, TrackingEvents } from "./types/tracking";

import { util } from "./util";
import { searchSuggest } from "./searchSuggest";
import { suggestTracking } from "./suggestTracking";
import { CLASSNAME, DATA, extractTrackparameterFromUrl, SELECTOR, splitTrackingLabelsFromUrl } from "./helper";
import { FALLBACK_SEARCH_URL } from "./types/constants";
import { Queue } from "./PromiseQueue";

export type DelayedAction = (() => void) | null;

export class SearchForm {
  formField: HTMLFormElement;
  deleteButton: HTMLDivElement;
  searchBar: HTMLDivElement;
  searchField: HTMLInputElement;
  submitButton: HTMLDivElement;
  placeholder: string;
  errorString: string | null;
  originalSearchTerm: string | null;

  constructor(form: HTMLFormElement, delayedAction: DelayedAction = null) {
    this.formField = form;
    this.deleteButton = this.formField.querySelector(SELECTOR.DELETE_BUTTON) as HTMLDivElement;
    this.searchBar = this.formField.querySelector(SELECTOR.SEARCHBAR) as HTMLDivElement;
    this.searchField = this.formField.querySelector(SELECTOR.SEARCH_FIELD) as HTMLInputElement;
    this.submitButton = this.formField.querySelector(SELECTOR.SUBMIT_BUTTON) as HTMLDivElement;

    this.placeholder = this.searchField.placeholder || this.searchField.getAttribute("placeholder") || "";
    this.errorString = this.searchField.getAttribute(DATA.ERROR);
    this.originalSearchTerm = "";

    if (Boolean(this.deleteButton).valueOf()) {
      this.toggleButtonVisibility();
      this.searchField.addEventListener("keyup", window.o_util.core.bind(this, this.toggleButtonVisibility));
      this.formField.addEventListener("reset", window.o_util.core.bind(this, this.resetHandler));
    }

    this.formField.addEventListener(
      "submit",
      ((e: SubmitEvent) => {
        e.preventDefault();
        this.submitSearch();
      }).bind(this)
    );

    this.searchField.addEventListener(
      "focus",
      (() => {
        this.clearFormField();
        this.searchField.classList.remove(CLASSNAME.HINT);

        /*🤫*/
        /*🤫*/
        this.searchField.parentNode.parentNode.classList.add("focus");
      }).bind(this)
    );

    if (document.activeElement && document.activeElement === this.searchField) {
      this.clearFormField();
      this.searchField.classList.remove(CLASSNAME.HINT);
    }

    /*🤫*/
    /*🤫*/
    if (typeof delayedAction === "function") {
      delayedAction();
    }

    this.enableSearch();
  }

  getSearchField(): HTMLInputElement {
    return this.searchField;
  }

  getValue(): string {
    return this.searchField.value;
  }

  setValue(val: string) {
    this.searchField.value = val;
    searchSuggest.setOldSearchTerm(val);
  }

  setSearchFieldAttribute(key: string, val: string) {
    this.searchField.setAttribute(key, val);
  }

  isOnDefault(): boolean {
    return this.getValue() === this.errorString || this.getValue() === this.placeholder;
  }

  isEmpty(): boolean {
    return this.getValue().length < 1 || this.isOnDefault();
  }

  hideButtons() {
    this.searchBar.classList.remove(CLASSNAME.SEARCHBAR_SHOW_BUTTONS);
  }

  showButtons() {
    this.searchBar.classList.add(CLASSNAME.SEARCHBAR_SHOW_BUTTONS);
  }

  focusSearchField() {
    this.searchField.focus();
  }

  resetHandler() {
    this.hideButtons();
    this.focusSearchField();
  }

  toggleButtonVisibility() {
    if (this.isEmpty()) {
      this.hideButtons();
    } else {
      this.showButtons();
    }
  }

  clearFormField() {
    if (this.isOnDefault()) {
      this.setValue("");
    }
  }

  cleanInputField() {
    this.setValue("");
    this.searchField.focus();
    this.hideButtons();

    window.o_global.events<TrackingEvents>().emit("tracking.bct.submitEvent", {
      san_Header: "search_x",
    });
  }

  fillDefaultText() {
    if (this.getValue() === "") {
      this.searchField.placeholder = this.placeholder;
      this.searchField.classList.add(CLASSNAME.HINT);
    } else {
      this.searchField.classList.remove(CLASSNAME.HINT);
    }
  }

  enableSearch() {
    this.searchField.removeAttribute("disabled");
    this.submitButton.removeAttribute("disabled");

    this.deleteButton.addEventListener("click", this.cleanInputField.bind(this));
    this.submitButton.addEventListener("click", this.submitSearch.bind(this));
  }

  checkAndGetIfArticleNumber(articleNumber: string): string | null {
    const regexp = /^(\.?\d{6}\w{0,2})$/g; /*🤫*/
    const regexpAcht = /^([1-9]\d{7}\w{2,3})$/g;
    articleNumber = articleNumber.replace(/\s+/g, "");
    let match: string[] | null = regexp.exec(articleNumber);
    if (match) {
      return match[1];
    }
    match = regexpAcht.exec(articleNumber);
    return match ? match[1] : null;
  }

  articleNumberSearch(action: string, trackingParameters: TrackingData, searchFieldValue: string): string {
    const articleNumberSearchUrl: string | null = this.formField.getAttribute(DATA.ARTICLE_NUMBER_SEARCH);
    const articleNumber: string | null = this.checkAndGetIfArticleNumber(searchFieldValue);

    if (articleNumber) {
      action = articleNumberSearchUrl + "?articlenumber=" + encodeURIComponent(articleNumber);
      trackingParameters["wk.san_Fallback"] = "ID";

      trackingParameters["wk.san_CountHits"] = 1;
      trackingParameters["wk.san_SearchTerm"] = articleNumber;
      trackingParameters["wk.san_SearchWordCount"] = 1;
    }
    return action;
  }

  sleep(ms: number) {
    return new Promise((r) => setTimeout(r, ms));
  }

  async submitSearch() {
    const searchFieldValue = util.string.trim(this.searchField.value);

    /*🤫*/
    let retries = 0;
    while (
      util.string.trim(this.searchField.getAttribute(DATA.TARGET_TERM) + "") !== searchFieldValue &&
      retries <= window.o_squirrel.failingRequestRetriesOnSubmit
    ) {
      await this.sleep(50);
      retries++;
    }

    const trackingParameters: TrackingData = { san_PageLoadComplete: "true", "wk.san_SearchType": "search" };
    let action: string = this.searchField.getAttribute(DATA.TARGET) as string;
    /*🤫*/
    if (retries === window.o_squirrel.failingRequestRetriesOnSubmit + 1 || !action) {
      action = FALLBACK_SEARCH_URL(searchFieldValue);
    }

    /*🤫*/
    if (!this.isEmpty()) {
      /*🤫*/
      action = this.articleNumberSearch(action, trackingParameters, searchFieldValue);
      const [url, tValue] = splitTrackingLabelsFromUrl(action);
      const hashTrackingParameters: TrackingData = extractTrackparameterFromUrl(tValue);

      const isModified: boolean = this.formField.classList.contains(CLASSNAME.TRACK_MODIFIED);
      const isTrackSuggest: boolean = this.formField.classList.contains(CLASSNAME.TRACK_SUGGEST);
      suggestTracking.trackUsage(
        trackingParameters,
        hashTrackingParameters,
        isModified,
        isTrackSuggest,
        false,
        this.searchField.getAttribute(DATA.SUGGEST_SEARCH_TERM)
      );

      util.global.setLocation(url);
    }
  }
}

export const searchForm = {
  init: (form: HTMLFormElement, delayedAction?: DelayedAction) => new SearchForm(form, delayedAction),
};
